// Location: assets/bal-reveal.js
// What: Reveals sections once and handles collection product anchor navigation.
// Why: Reveals content while preserving section backgrounds and sticky KV geometry.
// RELEVANT FILES: assets/bal-reveal.css, layout/theme.liquid, sections/main-collection-product-grid.liquid
(() => {
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const observed = new WeakSet();
  // Animate the content inside known BAL shells, never their full-width background.
  const CONTENT_SELECTOR = [
    ':scope > .bal-shelf > .bal-shelf__inner', ':scope > .sc > .sc__inner',
    ':scope > .bal-link-grid > .bal-link-grid__inner', ':scope > .bal-product-grid > .bal-product-grid__inner',
    ':scope > .bal-brand-row > .bal-brand-row__inner', ':scope > .cd > .cd__inner',
    ':scope > .bal-gift-banner > .bal-gift-banner__inner', ':scope > .bal-journal > .bal-journal__inner',
    ':scope > .brand-collection > .brand-collection__wrap',
    ':scope > .bal-brand-top > .bal-brand-top__inner', ':scope > .bal-brand-world > .bal-brand-world__inner',
    ':scope > .bal-statement > .bal-statement__inner', ':scope > .bal-pair > .bal-pair__inner',
    ':scope > .bal-kv > .bal-kv__viewport', ':scope > .collection-hero > .collection-hero__inner',
    ':scope > [class*="section-"][class*="-padding"] > :is(div, facet-filters-form)',
    ':scope > .bal-newsletter > .bal-newsletter__inner', ':scope > bal-logo-marquee > .bal-logo-marquee__viewport'
  ].join(', ');
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('bal-reveal--visible');
      observer.unobserve(entry.target);
    });
  }, { threshold: 0, rootMargin: '0px 0px -32px 0px' }) : null;

  function initialize() {
    document.querySelectorAll('#MainContent > .shopify-section').forEach((section) => {
      if (observed.has(section) || section.matches('.bal-kv-shelf-section, .section-kv-shelf') || section.querySelector('.bal-kv-shelf, [data-bal-kv], [data-kv]')) return;
      observed.add(section);
      section.classList.add('bal-reveal');
      const content = section.querySelectorAll(CONTENT_SELECTOR);
      if (content.length) {
        section.classList.add('bal-reveal--content');
        content.forEach((element) => element.classList.add('bal-reveal__content'));
      }
      const bounds = section.getBoundingClientRect();
      const inViewport = bounds.top < window.innerHeight && bounds.bottom > 0;
      if (inViewport || motion.matches || !observer || window.Shopify?.designMode) {
        section.classList.add('bal-reveal--visible');
      } else {
        section.classList.add('bal-reveal--pending');
        observer.observe(section);
      }
    });
  }

  motion.addEventListener('change', () => {
    if (!motion.matches) return;
    document.querySelectorAll('.bal-reveal').forEach((section) => {
      section.classList.add('bal-reveal--visible');
      observer?.unobserve(section);
    });
  });

  function revealTarget(target) {
    const section = target?.closest('.bal-reveal');
    if (!section) return;
    section.classList.add('bal-reveal--visible', 'bal-reveal--anchor');
    observer?.unobserve(section);
  }

  document.addEventListener('focusin', (event) => revealTarget(event.target));

  let navigation = 0;
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.bal-pair a[href^="#"]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    let id;
    try { id = decodeURIComponent(link.hash.slice(1)); } catch { return; }
    const target = document.getElementById(id);
    if (!target) return;
    event.preventDefault();
    const token = ++navigation;
    const section = target.closest('.bal-reveal');
    if (section) {
      section.classList.add('bal-reveal--visible', 'bal-reveal--anchor');
      observer?.unobserve(section);
      void section.offsetHeight;
    }
    history.replaceState(null, '', link.hash);
    target.scrollIntoView({ behavior: motion.matches ? 'auto' : 'smooth', block: 'start' });
    if (motion.matches) {
      target.focus({ preventScroll: true });
      return;
    }
    const started = performance.now();
    let previous = window.scrollY;
    let stable = 0;
    function onArrival(now) {
      if (token !== navigation || now - started > 4000) return;
      const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      const remaining = document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
      const top = target.getBoundingClientRect().top;
      // The existing sticky header changes height during scrolling.
      const arrived = Math.abs(top - margin) < 64 || remaining < 3;
      stable = Math.abs(window.scrollY - previous) < 0.5 ? stable + 1 : 0;
      previous = window.scrollY;
      if (arrived && stable >= 6 && now - started > 180) {
        target.focus({ preventScroll: true });
        if (id === 'products') {
          target.classList.remove('bal-products-highlight');
          void target.offsetWidth;
          target.classList.add('bal-products-highlight');
          target.addEventListener('animationend', () => target.classList.remove('bal-products-highlight'), { once: true });
        }
        return;
      }
      requestAnimationFrame(onArrival);
    }
    requestAnimationFrame(onArrival);
  });
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:reorder', initialize);
  initialize();
  if (window.location.hash) {
    try { revealTarget(document.getElementById(decodeURIComponent(window.location.hash.slice(1)))); }
    catch { /* Ignore malformed fragment identifiers. */ }
  }
})();
